import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { Loader } from 'components/atoms/Loader';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useSearchParams } from 'react-router-dom';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import {
  ILegalEntityExtendedAddress,
  ILegalEntityLicenceDetails,
  ILegalEntityLicenceRenewFormValues,
} from 'models/legalEntity';
import LicenceStore from 'state/LicenceStore';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { ILicenceSafeStorage } from 'models/licenceConfigurator';
import { useTranslations } from 'hooks/useTranslations';
import { sanitizeRenewFormData } from 'utils/storeDataTransforms';
import { useLegalEntities } from '../hooks';
import { LicenceRenewForm } from './licenceRenewForm';
import { hasExtendedConditions } from './typeGuards';
import { useRenewLicence } from './hooks';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';

export interface IObject {
  [key: string]: boolean;
}

export interface IDocument {
  name: string;
  weaponLicenceDocumentType: string;
  comment: string;
  file: File | null;
}
const initialFormValues: ILegalEntityLicenceRenewFormValues = {
  suitabilityAssessmentPassed: false,
  general: {
    startDate: '',
    expiryDate: null,
  },
  conditions: {
    weaponNumberLimit: 0,
    goodReasonUniqueIds: null,
    goodReason: '',
    areaOfValidityUniqueId: '',
    competencyUniqueId: '',
    hasSafeStorage: null,
    safeStorageDescription: '',
    safeStorages: [],
    weaponCategorisationUniqueId: '',
    carry: '',
    additionalConditions: '',
  },
  firearm: {
    firearmLinkToBeRemovedUniqueIds: [],
  },
  caseUniqueId: '',
  documents: [],
  galleryEntryUniqueId: null,
  additionalInformation: null,
  version: '',
};

interface ILicenceRenewEdit {
  id: string;
  caseId: string;
  licenceTemplateId: string;
  licenceDetails: ILegalEntityLicenceDetails;
  backLink: (licenceIdToNavigate: string) => void;
  errorType: () => void;
  breadcrumbs: any;
}

const LicenceRenewEdit = ({
  id,
  caseId,
  licenceTemplateId,
  licenceDetails,
  backLink,
  errorType,
  breadcrumbs,
}: ILicenceRenewEdit) => {
  const { selectedLanguage } = useLang();
  const { t } = useTranslations();
  const [formValues, setFormValues] = useState<ILegalEntityLicenceRenewFormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>(null);
  const [accessibleGoodReason, setAccessibleGoodReason] = useState<IDictionary[]>([]);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>(null);
  const [renewalPriceAvailable, setRenewalPriceAvailable] = useState<boolean | null>(null);
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { getCaseData, caseData } = useCase();
  const { getLicence, licence, loadDictionaries, weaponLicenceGoodReason } =
    useConfigurationLicence();
  const { getLegalEntityAddressesData, legalEntityAddress } = useLegalEntities();
  const { getLicenceApplicationProccessData } = useRenewLicence();
  const [primaryAddress, setPrimaryAddress] = useState<ILegalEntityExtendedAddress>();

  useEffect(() => {
    (async () => {
      await getCaseData(caseId);
    })();
  }, [caseId]);

  useEffect(() => {
    if (licenceDetails && licenceDetails.uniqueId) {
      LicenceStore.prepareForNewOperation(licenceDetails.uniqueId as string);
    }
  }, [licenceDetails?.uniqueId]);

  const saveToStory = (formData: any) => {
    LicenceStore.setFormData(formData, licenceDetails.uniqueId);
  };

  useEffect(() => {
    (async function getLegalEntityAddressInit() {
      if (id) {
        await getLegalEntityAddressesData(id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const address = legalEntityAddress.filter((item) => item.isDefault);
    if (address && address.length) {
      setPrimaryAddress(address[0]);
    } else {
      setPrimaryAddress(undefined);
    }
  }, [legalEntityAddress]);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.weaponLicenceGoodReason]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function getDataInit() {
      setIsLoading(true);
      if (licenceTemplateId && id) {
        await getLicence(licenceTemplateId);
      }
      setIsLoading(false);
    })();
  }, [licenceTemplateId]);

  useEffect(() => {
    (async function fetchRenewalFee() {
      if (licence && licenceTemplateId) {
        const prices = await getLicenceApplicationProccessData(licenceTemplateId);

        if (prices && prices.length > 0) {
          const renewalFee = parseFloat(prices[0].priceFee);
          licence.general = {
            ...licence.general,
            renewalFee,
          };
          setRenewalPriceAvailable(true);
        } else {
          setRenewalPriceAvailable(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licence, licenceTemplateId]);

  useEffect(() => {
    if (type === ELegalEntityTypes.individual) {
      (async function getLegalEntityDataInit() {
        setIsLoading(true);
        if (id) {
          const response = await getLegalEntity(id);
          if (response) {
            setLegalEntity(response);
          }
          setIsLoading(false);
        }
      })();
      setOrgLegalEntity(null);
    } else if (type === ELegalEntityTypes.organisation) {
      (async function getLegalEntityDataInit() {
        setIsLoading(true);
        if (id) {
          const response = await getOrgLegalEntity(id);
          if (response) {
            setOrgLegalEntity(response);
          }
          setIsLoading(false);
        }
      })();
      setLegalEntity(null);
    } else {
      errorType();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!formValues && weaponLicenceGoodReason?.length && licence && licenceDetails) {
      const dataFromStore = LicenceStore.getFormData(licenceDetails.uniqueId);

      if (dataFromStore) {
        const transformedData = sanitizeRenewFormData(dataFromStore);
        setFormValues(transformedData);
      } else {
        LicenceStore.setLicenceId(licenceDetails.uniqueId);
        let goodReasonUniqueIds = null;
        if (licence.conditions.goodReasonDictionaryIds.length) {
          const accessibleWeaponLicenceGoodReason = weaponLicenceGoodReason.filter((item) =>
            licence.conditions.goodReasonDictionaryIds.includes(item.uniqueId.toString()),
          );
          const checkedGoodReasonIds = licenceDetails.conditions.goodReasonUniqueIds;
          setAccessibleGoodReason(accessibleWeaponLicenceGoodReason);
          goodReasonUniqueIds = accessibleWeaponLicenceGoodReason.reduce(
            (acc, item) => {
              acc[item.uniqueId.toString()] = checkedGoodReasonIds.includes(
                item.uniqueId.toString(),
              );
              return acc;
            },
            {} as Record<string, boolean>,
          );
        }

        let initialSafeStorages: ILicenceSafeStorage[] = [];

        if (hasExtendedConditions(licenceDetails.conditions)) {
          const existingSafeStorages = licenceDetails.conditions.safeStorages;
          if (existingSafeStorages && existingSafeStorages.length > 0) {
            initialSafeStorages = existingSafeStorages;
            LicenceStore.setFormSafeStorages(existingSafeStorages, licenceDetails.uniqueId);
          }
        }

        const newFormData = {
          ...initialFormValues,
          general: {
            ...initialFormValues.general,
            licenceAuthorityUniqueId: licenceDetails.general.licenceAuthorityUniqueId ?? '',
            secondaryLicenceAuthorityUniqueId:
              licenceDetails.general.secondaryLicenceAuthorityUniqueId ?? '',
          },
          firearm: {
            ...initialFormValues.firearm,
            ...licenceDetails.firearm,
          },
          conditions: {
            ...initialFormValues.conditions,
            ...licenceDetails.conditions,
            goodReasonUniqueIds,
            safeStorages: initialSafeStorages,
            areaOfValidityUniqueId: licence.conditions.specificCountryArea
              ? '1'
              : licenceDetails.conditions.areaOfValidityUniqueId,
          },
          additionalInformation: licenceDetails.additionalInformation ?? null,
          version: licenceDetails.version,
        };

        setFormValues(newFormData);
        saveToStory(newFormData);
      }
    }
  }, [weaponLicenceGoodReason, licence, licenceDetails]);

  return (
    <div>
      {renewalPriceAvailable === false && (
        <Box sx={{ mb: 2 }}>
          <Alert
            variant={EAlertVariants.error}
            text={t('legalEntity.licenceRenewal.renewalPriceNotSet')}
          />
        </Box>
      )}

      {licence &&
        formValues &&
        caseData &&
        ((caseData.legalType === LETypes.individual && legalEntity) ||
          (caseData.legalType === LETypes.organization && orgLegalEntity)) &&
        !isLoading ? (
        <LicenceRenewForm
          licence={licence}
          legalEntityLicenceDetails={licenceDetails}
          legalEntity={
            caseData.legalType === LETypes.individual ? legalEntity : orgLegalEntity
          }
          initialFormValues={formValues}
          primaryAddress={primaryAddress}
          accessibleWeaponLicenceGoodReason={accessibleGoodReason}
          availableAddresses={legalEntityAddress}
          backLink={backLink}
          breadcrumbs={breadcrumbs}
          isRenewalPriceUnavailable={!renewalPriceAvailable}
          type={caseData.legalType as LETypes}
        />
      ) : (
        <Loader isLoading />
      )}
    </div>
  );
};

export { LicenceRenewEdit };
